import React, { useState, useEffect } from "react";
import axios from "axios";

const API_URL = "https://stockaadvikbackend.aadviktech.com";

interface Invoice {
  id: number;
  farm: string;
  party: string;
  invoice_type: string;
  date: string;
  datainvoice: string;
}

interface DataInvoiceRow {
  id: number;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  category: string;
  investment_type: string;
  total_amount_category: string;
}

interface SellDataInvoiceRow {
  stock_id: string;
  stock_name: string;
  category: string;
  sell_units: number;
  sell_amount: number;
  indirect_charges: string;
  total_amount: number;
  date: string;
}

interface SellStockRow {
  id: number;
  invoice_id: number;
  stock_name: string;
  quantity: number;
  price_per_unit: number;
  total_amount: number;
  sell_unit?: string;
}

interface Party {
  id: number;
  name: string;
  number: number;
  contact_person_name: string;
  contact_person_number: number;
  categories: string;
  date: string;
  total_amount: number;
  deleteparty: number;
}

interface NormalisedRow {
  stock_name: string;
  unit: string | number;
  perUnitCharge: string | number;
  total_amount: string | number;
}

// ─── Edit form row types ──────────────────────────────────────────────────────
interface EditPurchaseRow {
  stock_name: string;
  unit: string;
  perUnitCharge: string;
  total_amount: string;
  indirect_charges: string;
  category: string;
}

interface EditSellRow {
  stock_name: string;
  sell_units: string;
  sell_amount: string;
  indirect_charges: string;
  total_amount: string;
}

interface EditFormState {
  farm: string;
  party: string;
  date: string;
  purchaseRows: EditPurchaseRow[];
  sellRows: EditSellRow[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function toCurrency(val: number | string) {
  const n = Number(val) || 0;
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return dateString;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function toInputDate(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return "";
  return d.toISOString().split("T")[0];
}

function numberToWords(num: number): string {
  if (isNaN(num)) return "";
  if (num === 0) return "Zero";
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const g = ["", "Thousand", "Lakh", "Crore", "Arab", "Kharab"];
  const n = Math.floor(num);

  function _inWordsHundred(n: number) {
    let str = "";
    if (n > 99) { str += a[Math.floor(n / 100)] + " Hundred "; n = n % 100; }
    if (n > 19) { str += b[Math.floor(n / 10)] + " " + a[n % 10]; }
    else { str += a[n]; }
    return str.trim();
  }

  let i = 0;
  const words: string[] = [];
  let remaining = n;
  if (remaining === 0) return "Zero";
  while (remaining > 0 && i < g.length) {
    let segment: number;
    if (i === 0) { segment = remaining % 1000; remaining = Math.floor(remaining / 1000); }
    else { segment = remaining % 100; remaining = Math.floor(remaining / 100); }
    if (segment) words.push(_inWordsHundred(segment) + (g[i] ? " " + g[i] : ""));
    i++;
  }
  const s = words.reverse().join(" ").replace(/\s+/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const INVOICE_TYPES = [
  { key: "ALL", label: "All Types" },
  { key: "Sell", label: "Sell" },
  { key: "Purchase", label: "Purchase" },
];

const colorBadge = (type: string) => {
  switch (type.toLowerCase()) {
    case "sell":     return "bg-green-50 text-green-700 border-green-400";
    case "purchase": return "bg-yellow-50 text-yellow-800 border-yellow-400";
    default:         return "bg-slate-100 text-slate-900 border-slate-400";
  }
};

// ─── Empty row factories ──────────────────────────────────────────────────────
const emptyPurchaseRow = (): EditPurchaseRow => ({
  stock_name: "", unit: "", perUnitCharge: "", total_amount: "", indirect_charges: "", category: "",
});
const emptySellRow = (): EditSellRow => ({
  stock_name: "", sell_units: "", sell_amount: "", indirect_charges: "", total_amount: "",
});

// ─── Component ────────────────────────────────────────────────────────────────
const InvoicePage: React.FC = () => {
  const [invoices, setInvoices]           = useState<Invoice[]>([]);
  const [parties, setParties]             = useState<Party[]>([]);
  const [sellStocksMap, setSellStocksMap] = useState<Record<number, SellStockRow[]>>({});
  const [loading, setLoading]             = useState(false);
  const [partiesLoading, setPartiesLoading] = useState(false);
  const [errorMsg, setErrorMsg]           = useState("");
  const [searchText, setSearchText]       = useState("");
  const [viewingInvoice, setViewingInvoice]   = useState<Invoice | null>(null);
  const [deletingInvoice, setDeletingInvoice] = useState<Invoice | null>(null);
  const [deleting, setDeleting]           = useState(false);
  const [editingInvoice, setEditingInvoice]   = useState<Invoice | null>(null);
  const [editForm, setEditForm]           = useState<EditFormState | null>(null);
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState("");

  const [filterType,  setFilterType]  = useState("ALL");
  const [filterParty, setFilterParty] = useState("ALL");
  const [filterFarm,  setFilterFarm]  = useState("ALL");
  const [filterStock, setFilterStock] = useState("ALL");

  const [uniqueFarms,      setUniqueFarms]      = useState<string[]>([]);
  const [uniqueStockNames, setUniqueStockNames] = useState<string[]>([]);
  const [totalSell,     setTotalSell]     = useState(0);
  const [totalPurchase, setTotalPurchase] = useState(0);

  useEffect(() => { fetchParties(); fetchInvoices(); fetchAllSellStocks(); }, []);

  useEffect(() => {
    const farmSet  = new Set<string>();
    const stockSet = new Set<string>();
    let sell = 0, purchase = 0;

    invoices.forEach(inv => {
      if (inv.farm?.trim()) farmSet.add(inv.farm.trim());
      const isSell     = inv.invoice_type?.toLowerCase() === "sell";
      const isPurchase = inv.invoice_type?.toLowerCase() === "purchase";

      if (isSell) {
        const rows = getInvoiceRows(inv);
        rows.forEach(r => {
          if (r.stock_name?.trim()) stockSet.add(r.stock_name.trim());
          sell += Number(r.total_amount || 0);
        });
      } else if (isPurchase) {
        try {
          const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
          rows.forEach(r => {
            if (r.stock_name?.trim()) stockSet.add(r.stock_name.trim());
            purchase += Number(r.total_amount || 0);
          });
        } catch {}
      }
    });

    setUniqueFarms(Array.from(farmSet));
    setUniqueStockNames(Array.from(stockSet));
    setTotalSell(sell);
    setTotalPurchase(purchase);
  }, [invoices, sellStocksMap]);

  function fetchParties() {
    setPartiesLoading(true);
    axios.get(`${API_URL}/auth/addparty`)
      .then(res => setParties(Array.isArray(res.data) ? (res.data as Party[]) : []))
      .catch(() => setParties([]))
      .finally(() => setPartiesLoading(false));
  }

  function fetchInvoices() {
    setLoading(true); setErrorMsg("");
    axios.get(`${API_URL}/auth/invoice`)
      .then(res => {
        setInvoices(Array.isArray(res.data) ? (res.data as Invoice[]) : []);
        if (!Array.isArray(res.data)) setErrorMsg("Invalid data received");
      })
      .catch(() => setErrorMsg("Failed to fetch invoices"))
      .finally(() => setLoading(false));
  }

  function fetchAllSellStocks() {
    axios.get(`${API_URL}/auth/sellstocks`)
      .then(res => {
        if (!Array.isArray(res.data)) return;
        const rows = res.data as SellStockRow[];
        const map: Record<number, SellStockRow[]> = {};
        rows.forEach(row => {
          const key = row.invoice_id;
          if (!map[key]) map[key] = [];
          map[key].push(row);
        });
        setSellStocksMap(map);
      })
      .catch(() => console.error("Failed to fetch sell stocks"));
  }

  function getInvoiceRows(inv: Invoice): NormalisedRow[] {
    const isSell = inv.invoice_type?.toLowerCase() === "sell";

    if (isSell) {
      const apiRows = sellStocksMap[inv.id] || [];
      if (apiRows.length > 0) {
        return apiRows.map(r => ({
          stock_name:    r.stock_name,
          unit:          r.quantity ?? "",
          perUnitCharge: r.price_per_unit ?? "",
          total_amount:  r.total_amount ?? 0,
        }));
      }
      try {
        const rows: SellDataInvoiceRow[] = JSON.parse(inv.datainvoice);
        return rows.map(r => {
          const units    = Number(r.sell_units ?? 0);
          const totalAmt = Number(r.total_amount ?? r.sell_amount ?? 0);
          const perUnit  = units > 0 ? (totalAmt / units) : 0;
          return { stock_name: r.stock_name, unit: units, perUnitCharge: perUnit.toFixed(2), total_amount: totalAmt };
        });
      } catch { return []; }
    }

    try {
      const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
      return rows.map(r => ({
        stock_name: r.stock_name, unit: r.unit, perUnitCharge: r.perUnitCharge, total_amount: r.total_amount,
      }));
    } catch { return []; }
  }

  function getInvoiceTotalAndStocks(inv: Invoice) {
    const rows     = getInvoiceRows(inv);
    const totalAmt = rows.reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
    const stockString = rows.map(r => r.stock_name).filter(Boolean).join(", ");
    return { totalAmt, stockString };
  }

  function getPartyName(partyId: string | number | undefined): string {
    if (!partyId) return "";
    const party = parties.find(p => String(p.id) === String(partyId));
    return party ? party.name : `ID#${partyId}`;
  }

  // ─── Open edit modal ─────────────────────────────────────────────────────
  function openEditModal(inv: Invoice) {
    const isSell = inv.invoice_type?.toLowerCase() === "sell";
    const isPurchase = inv.invoice_type?.toLowerCase() === "purchase";

    const form: EditFormState = {
      farm: inv.farm || "",
      party: String(inv.party || ""),
      date: toInputDate(inv.date),
      purchaseRows: [],
      sellRows: [],
    };

    if (isPurchase) {
      try {
        const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
        form.purchaseRows = rows.map(r => ({
          stock_name: r.stock_name || "",
          unit: r.unit || "",
          perUnitCharge: r.perUnitCharge || "",
          total_amount: r.total_amount || "",
          indirect_charges: r.indirect_charges || "",
          category: r.category || "",
        }));
      } catch {}
      if (!form.purchaseRows.length) form.purchaseRows = [emptyPurchaseRow()];
    } else if (isSell) {
      // Try sellstocks API first
      const apiRows = sellStocksMap[inv.id] || [];
      if (apiRows.length > 0) {
        form.sellRows = apiRows.map(r => ({
          stock_name: r.stock_name || "",
          sell_units: String(r.quantity ?? ""),
          sell_amount: String(r.price_per_unit ?? ""),
          indirect_charges: "",
          total_amount: String(r.total_amount ?? ""),
        }));
      } else {
        try {
          const rows: SellDataInvoiceRow[] = JSON.parse(inv.datainvoice);
          form.sellRows = rows.map(r => ({
            stock_name: r.stock_name || "",
            sell_units: String(r.sell_units ?? ""),
            sell_amount: String(r.sell_amount ?? ""),
            indirect_charges: String(r.indirect_charges || ""),
            total_amount: String(r.total_amount ?? ""),
          }));
        } catch {}
      }
      if (!form.sellRows.length) form.sellRows = [emptySellRow()];
    }

    setEditingInvoice(inv);
    setEditForm(form);
    setSaveError("");
  }

  function closeEditModal() { setEditingInvoice(null); setEditForm(null); setSaveError(""); }

  // ─── Auto-calc totals on sell row change ─────────────────────────────────
  function recalcSellRow(row: EditSellRow): EditSellRow {
    const units  = parseFloat(row.sell_units) || 0;
    const amount = parseFloat(row.sell_amount) || 0;
    const charges = parseFloat(row.indirect_charges) || 0;
    const total = units * amount + charges;
    return { ...row, total_amount: total > 0 ? total.toFixed(2) : row.total_amount };
  }

  function recalcPurchaseRow(row: EditPurchaseRow): EditPurchaseRow {
    const units = parseFloat(row.unit) || 0;
    const per   = parseFloat(row.perUnitCharge) || 0;
    const charges = parseFloat(row.indirect_charges) || 0;
    const total = units * per + charges;
    return { ...row, total_amount: total > 0 ? total.toFixed(2) : row.total_amount };
  }

  // ─── Save edited invoice ─────────────────────────────────────────────────
  async function handleSaveEdit() {
    if (!editingInvoice || !editForm) return;
    setSaving(true); setSaveError("");

    const isSell = editingInvoice.invoice_type?.toLowerCase() === "sell";
    const isPurchase = editingInvoice.invoice_type?.toLowerCase() === "purchase";

    try {
      let datainvoice = editingInvoice.datainvoice;

      if (isPurchase) {
        // Rebuild datainvoice JSON for purchase
        const existing: DataInvoiceRow[] = (() => { try { return JSON.parse(editingInvoice.datainvoice); } catch { return []; } })();
        const updatedRows = editForm.purchaseRows.map((r, i) => ({
          ...(existing[i] || { id: i + 1, investment_type: "", total_amount_category: "" }),
          stock_name:    r.stock_name,
          unit:          r.unit,
          perUnitCharge: r.perUnitCharge,
          total_amount:  r.total_amount,
          indirect_charges: r.indirect_charges,
          category:      r.category,
        }));
        datainvoice = JSON.stringify(updatedRows);
      } else if (isSell) {
        // Update datainvoice JSON for sell
        const updatedSellRows = editForm.sellRows.map(r => ({
          stock_name:       r.stock_name,
          sell_units:       Number(r.sell_units) || 0,
          sell_amount:      Number(r.sell_amount) || 0,
          indirect_charges: r.indirect_charges,
          total_amount:     Number(r.total_amount) || 0,
          date:             editForm.date,
        }));
        datainvoice = JSON.stringify(updatedSellRows);

        // Also update sell_stocks records if they exist
        const apiRows = sellStocksMap[editingInvoice.id] || [];
        if (apiRows.length > 0) {
          await Promise.all(
            editForm.sellRows.map((r, i) => {
              const existingRow = apiRows[i];
              if (!existingRow) return Promise.resolve();
              return axios.put(`${API_URL}/auth/sellstocks/${existingRow.id}`, {
                stock_name:       r.stock_name,
                sell_units:       Number(r.sell_units) || 0,
                sell_amount:      Number(r.sell_amount) || 0,
                indirect_charges: r.indirect_charges,
                total_amount:     Number(r.total_amount) || 0,
              });
            })
          );
          // If edit form has more rows than existing, add new ones
          for (let i = apiRows.length; i < editForm.sellRows.length; i++) {
            const r = editForm.sellRows[i];
            await axios.post(`${API_URL}/auth/sellstocks`, {
              party_id:         editingInvoice.party,
              stock_name:       r.stock_name,
              sell_units:       Number(r.sell_units) || 0,
              sell_amount:      Number(r.sell_amount) || 0,
              indirect_charges: r.indirect_charges,
              date:             editForm.date,
            });
          }
          // If edit form has fewer rows than existing, delete the extras
          for (let i = editForm.sellRows.length; i < apiRows.length; i++) {
            await axios.delete(`${API_URL}/auth/sellstocks/${apiRows[i].id}`);
          }
        }
      }

      // Update the invoice record
      await axios.put(`${API_URL}/auth/invoice`, {
        id:           editingInvoice.id,
        farm:         editForm.farm,
        party:        editForm.party,
        date:         editForm.date,
        datainvoice,
      });

      // Refresh
      await fetchAllSellStocksAsync();
      setInvoices(prev => prev.map(inv =>
        inv.id === editingInvoice.id
          ? { ...inv, farm: editForm.farm, party: editForm.party, date: editForm.date, datainvoice }
          : inv
      ));

      closeEditModal();
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function fetchAllSellStocksAsync() {
    const res = await axios.get(`${API_URL}/auth/sellstocks`);
    if (!Array.isArray(res.data)) return;
    const rows = res.data as SellStockRow[];
    const map: Record<number, SellStockRow[]> = {};
    rows.forEach(row => {
      if (!map[row.invoice_id]) map[row.invoice_id] = [];
      map[row.invoice_id].push(row);
    });
    setSellStocksMap(map);
  }

  function openDeleteDialog(invoice: Invoice)  { setDeletingInvoice(invoice); }
  function closeDeleteDialog()                 { setDeletingInvoice(null); }

  function handleDeleteInvoice(invoiceId: number) {
    setDeleting(true);
    axios.delete(`${API_URL}/auth/invoice/${invoiceId}`)
      .then(() => {
        setInvoices(inv => inv.filter(i => i.id !== invoiceId));
        closeDeleteDialog();
        if (viewingInvoice?.id === invoiceId) setViewingInvoice(null);
      })
      .catch(() => setErrorMsg("Failed to delete invoice."))
      .finally(() => setDeleting(false));
  }

  const handlePrint = () => {
    const invoicePrintElem = document.getElementById("invoice-print");
    if (!invoicePrintElem) return;
    const printContents = invoicePrintElem.innerHTML;
    const printWindow   = window.open("", "", "width=900,height=700");
    if (printWindow) {
      printWindow.document.write(`
        <html><head><title>Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 4px 7px !important; background: #f7fafb; color: #222; font-size: 11.5px !important; margin: 0; line-height: 1.25; max-width: 830px; width: 100vw; box-sizing: border-box; }
            .text-xs { font-size: 10px !important; } .font-semibold { font-weight: 600; } .font-bold { font-weight: 700; } .font-extrabold { font-weight: 800; }
            .text-lg { font-size: 14px !important; } .text-xl { font-size: 15px !important; }
            .text-green-700 { color: #15803d; } .text-blue-900 { color: #1e3a8a;} .text-blue-800 { color: #1e40af;} .text-blue-700 { color: #1d4ed8;} .text-gray-700 { color: #374151;} .text-gray-800 { color: #1f2937;} .italic { font-style: italic; }
            .bg-blue-50 { background: #eff6ff; } .bg-blue-100 { background: #dbeafe; }
            .bg-gradient-to-tr { background: linear-gradient(135deg, #fff 0%, #eaeffe 40%, #f0f7ff 100%); }
            .bg-gray-50 { background: #f9fafb;} .bg-white { background: #fff;}
            .mb-1 { margin-bottom: 2px !important;} .ml-8 { margin-left: 16px !important;} .mb-2 { margin-bottom: 6px !important;} .mb-4 { margin-bottom: 10px !important;} .mt-1 { margin-top: 2px !important;} .mt-2 { margin-top: 4px !important;} .mt-4 { margin-top: 9px !important;}
            .rounded-2xl { border-radius: 0.65rem;} .rounded-xl { border-radius: 0.4rem;} .rounded-lg { border-radius: 0.25rem;} .rounded-bl-lg { border-bottom-left-radius: 0.25rem;} .rounded-br-lg { border-bottom-right-radius: 0.25rem;}
            .border { border: 1px solid #cbd5e1;} .border-blue-100 { border-color: #dbeafe;} .border-blue-200 { border-color: #bfdbfe;} .border-blue-300 { border-color: #93c5fd;} .border-gray-200 { border-color: #e5e7eb;}
            .shadow, .shadow-lg, .shadow-2xl, .shadow-xl { box-shadow: none !important;}
            .invoice-total { background: #e9fbe8 !important; font-size: 13px !important;}
            .w-full { width: 100%;} .w-1\/2 { width: 50%; } .align-top { vertical-align: top;} .text-right { text-align: right;} .text-center { text-align: center;}
            .flex { display: flex;} .flex-col { flex-direction: column;} .items-center { align-items: center;} .justify-between { justify-content: space-between;}
            .gap-5 { gap: 10px;} .overflow-auto { overflow: auto;}
            .p-2 {padding:5px!important;} .p-3 { padding: 7px !important;} .p-4 { padding: 7px !important;} .p-6 { padding: 14px !important;}
            .py-1 { padding-top: 2px !important; padding-bottom: 2px !important;} .py-2 { padding-top: 5px !important; padding-bottom: 5px !important;} .py-3 { padding-top: 7px !important; padding-bottom: 7px !important;}
            .px-2 { padding-left: 5px !important; padding-right: 5px !important;} .px-3 { padding-left: 8px !important; padding-right: 8px !important;} .px-6 { padding-left: 14px !important; padding-right: 14px !important;}
            .border-collapse { border-collapse: collapse;} .border-l-0 { border-left: 0;} .border-r { border-right: 1px solid #bfdbfe;}
            @media print {
              html, body { width: 100vw; max-width: 856px !important; margin: 0 auto !important; padding: 0 !important; font-size: 11.5px !important; }
              .shadow, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important;}
            }
          </style>
        </head>
        <body>${printContents}</body></html>
      `);
      printWindow.document.close();
      printWindow.onload = () => setTimeout(() => { printWindow.focus(); printWindow.print(); }, 350);
    }
  };

  const filteredInvoices = React.useMemo(() => {
    let filtered = invoices;
    if (filterType !== "ALL")
      filtered = filtered.filter(inv => inv.invoice_type?.trim().toLowerCase() === filterType.toLowerCase());
    if (filterParty !== "ALL")
      filtered = filtered.filter(inv => String(inv.party) === String(filterParty));
    if (filterFarm !== "ALL")
      filtered = filtered.filter(inv => inv.farm?.trim() === filterFarm);
    if (filterStock !== "ALL") {
      filtered = filtered.filter(inv => {
        const rows = getInvoiceRows(inv);
        return rows.some(r => r.stock_name?.trim() === filterStock);
      });
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(inv => {
        const rows = getInvoiceRows(inv);
        return (
          (inv.farm || "").toLowerCase().includes(q) ||
          getPartyName(inv.party).toLowerCase().includes(q) ||
          (inv.invoice_type || "").toLowerCase().includes(q) ||
          rows.some(r => (r.stock_name ?? "").toLowerCase().includes(q))
        );
      });
    }
    return filtered;
  }, [invoices, filterType, filterParty, filterFarm, filterStock, searchText, parties, sellStocksMap]);

  // ─── Input helpers ─────────────────────────────────────────────────────────
  const inputCls = "w-full px-2 py-1.5 rounded border border-gray-200 text-[12.5px] focus:outline-blue-400 bg-white";
  const labelCls = "block text-[11px] font-semibold text-blue-800 mb-0.5 uppercase tracking-wide";

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200">

      {/* SIDEBAR */}
      <div className="w-[260px] min-w-[190px] max-w-xs border-r bg-gradient-to-br from-green-50 via-white to-blue-100 flex flex-col px-4 py-6 shadow-sm gap-7 relative">
        <div>
          <div className="text-xl font-extrabold text-green-950 tracking-tight mb-2">Invoice Dashboard</div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="p-3 rounded border-l-4 border-blue-500 bg-blue-50">
            <div className="text-[12px] text-blue-700 uppercase tracking-wide font-semibold">Total Sell</div>
            <div className="text-blue-900 text-xl font-extrabold">₹ {toCurrency(totalSell)}</div>
          </div>
          <div className="p-3 rounded border-l-4 border-yellow-500 bg-yellow-50">
            <div className="text-[12px] text-yellow-700 uppercase tracking-wide font-semibold">Total Purchase</div>
            <div className="text-yellow-900 text-xl font-extrabold">₹ {toCurrency(totalPurchase)}</div>
          </div>
          <div className="p-3 rounded border-l-4 border-gray-400 bg-gray-50">
            <div className="text-[12px] text-gray-600 uppercase tracking-wide font-semibold">Invoices</div>
            <div className="text-gray-900 text-xl font-extrabold">{filteredInvoices.length}</div>
          </div>
        </div>
        <div className="flex-1" />
        <div className="pt-8">
          <div className="text-[11.5px] text-gray-400 font-mono leading-relaxed">
            Tip: Filter like a pro—try part of name or date!
            <br />Use <span className="bg-blue-100 px-2 py-0.5 rounded">View</span> for details.
          </div>
          <div className="absolute bottom-2 left-4 right-4 text-slate-300 font-mono text-[10.6px] select-none">
            Stock Aadvik Ledger · Tally UI
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Header & Filters */}
        <div className="flex flex-col md:flex-row items-center justify-between px-7 pt-7 pb-3 bg-gradient-to-br from-white to-blue-100 border-b gap-4">
          <div>
            <div className="text-xl font-extrabold text-blue-900 tracking-tight mb-1 leading-7">Stock Invoice Ledger</div>
            <div className="text-gray-500 text-[12.3px]">Accountant-style — Review, tally and print quickly.</div>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            <input
              type="text"
              className="px-2.5 py-2 rounded-lg border border-gray-200 bg-white shadow focus:outline-blue-400 w-[180px] text-[13.1px]"
              placeholder="Search (Party, Stock, Farm…)"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[105px]">
              {INVOICE_TYPES.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
            </select>
            <select value={filterParty} onChange={e => setFilterParty(e.target.value)} className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[140px]">
              <option value="ALL">All Parties</option>
              {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={filterFarm} onChange={e => setFilterFarm(e.target.value)} className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[110px]">
              <option value="ALL">All Farms</option>
              {uniqueFarms.map((farm, idx) => <option key={farm + idx} value={farm}>{farm}</option>)}
            </select>
            <select value={filterStock} onChange={e => setFilterStock(e.target.value)} className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[150px]">
              <option value="ALL">All Stocks</option>
              {uniqueStockNames.map((stock, idx) => <option key={stock + idx} value={stock}>{stock}</option>)}
            </select>
            <button
              className="ml-1 px-4 py-2 rounded-lg font-semibold border text-sm transition bg-gray-50 text-blue-800 border-gray-300 hover:bg-blue-50 hover:text-blue-900"
              onClick={() => { setFilterType("ALL"); setFilterParty("ALL"); setFilterFarm("ALL"); setFilterStock("ALL"); setSearchText(""); }}
              type="button"
            >Reset</button>
          </div>
        </div>

        {errorMsg && (
          <div className="mb-4 p-2 rounded border text-center shadow bg-red-50 text-red-700 border-red-200 mx-7">{errorMsg}</div>
        )}

        {/* Ledger Table */}
        <div className="h-full overflow-y-auto px-3 pt-3 pb-8 flex-1">
          <div className="rounded-2xl bg-white/90 border border-blue-200 shadow-2xl overflow-auto">
            <table className="min-w-[1200px] w-full table-auto border-separate border-spacing-y-2">
              <thead className="sticky top-0 bg-gradient-to-br from-blue-50 via-white to-gray-100 shadow z-10">
                <tr className="text-[15px] font-bold tracking-tight text-blue-900 border-b-2 border-blue-400/30">
                  <th className="py-3 px-4 text-center">#</th>
                  <th className="py-3 px-4 text-center">Date</th>
                  <th className="py-3 px-4 text-center">Type</th>
                  <th className="py-3 px-4 text-center">Farm</th>
                  <th className="py-3 px-4 text-center">Party</th>
                  <th className="py-3 px-4 text-center">Stock(s)</th>
                  <th className="py-3 px-4 text-center">Amount (₹)</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(loading || partiesLoading) ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400 text-lg">Loading...</td></tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr><td colSpan={8} className="py-12 text-center text-gray-400 text-lg">No invoices found.</td></tr>
                ) : (
                  filteredInvoices.map((inv, idx) => {
                    const { totalAmt, stockString } = getInvoiceTotalAndStocks(inv);
                    return (
                      <tr
                        key={inv.id}
                        className="hover:bg-yellow-50/70 even:bg-blue-50/30 transition-all border border-slate-100 rounded-xl"
                        style={{ cursor: "pointer" }}
                        tabIndex={0}
                        onDoubleClick={() => setViewingInvoice(inv)}
                      >
                        <td className="text-blue-900 text-center font-mono py-3 px-4">{idx + 1}</td>
                        <td className="text-center py-3 px-4">{formatDate(inv.date)}</td>
                        <td className="text-center py-3 px-4">
                          <span className={`inline-block px-2 py-0.5 rounded font-semibold border text-xs ${colorBadge(inv.invoice_type)}`}>
                            {inv.invoice_type}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">{inv.farm}</td>
                        <td className="text-center py-3 px-4">{getPartyName(inv.party)}</td>
                        <td className="text-center truncate max-w-xs py-3 px-4" title={stockString}>{stockString || "—"}</td>
                        <td className="text-center font-mono text-blue-900 font-bold py-3 px-4">₹ {toCurrency(totalAmt)}</td>
                        <td className="text-center py-3 px-4">
                          <div className="flex justify-center gap-2">
                            <button
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg text-white text-xs font-bold hover:from-blue-600 hover:to-blue-900 transition shadow"
                              onClick={e => { e.stopPropagation(); setViewingInvoice(inv); }}
                            >View</button>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-white text-xs font-bold hover:from-amber-600 hover:to-amber-800 transition shadow"
                              onClick={e => { e.stopPropagation(); openEditModal(inv); }}
                            >Edit</button>
                            <button
                              className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-700 rounded-lg text-white text-xs font-bold hover:from-red-700 hover:to-red-800 transition shadow hidden"
                              onClick={e => { e.stopPropagation(); openDeleteDialog(inv); }}
                            >Delete</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Delete Dialog ─────────────────────────────────────────────────── */}
      {deletingInvoice && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-xl shadow-lg p-8 text-center max-w-sm w-full border border-red-200">
            <div className="text-lg font-bold text-red-800 mb-2">Delete Invoice</div>
            <div className="text-gray-800 mb-4">
              Are you sure you want to delete invoice <b>#{deletingInvoice.id}</b>?<br />This action cannot be undone.
            </div>
            <div className="flex items-center justify-center gap-4">
              <button className="px-4 py-2 rounded-lg bg-gray-300 text-gray-800 font-semibold hover:bg-gray-400 transition" onClick={closeDeleteDialog} disabled={deleting}>Cancel</button>
              <button className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-800 transition" onClick={() => handleDeleteInvoice(deletingInvoice.id)} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ─────────────────────────────────────────────────────── */}
      {editingInvoice && editForm && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={closeEditModal} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-blue-200 w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden mx-3">

            {/* Edit Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-gradient-to-r from-amber-50 to-white flex-shrink-0">
              <div>
                <div className="text-lg font-extrabold text-amber-800">Edit Invoice #{editingInvoice.id}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`inline-block px-2 py-0.5 rounded font-semibold border text-xs ${colorBadge(editingInvoice.invoice_type)}`}>
                    {editingInvoice.invoice_type}
                  </span>
                  <span className="text-[12px] text-gray-500">Changes will update invoice &amp; related stock records</span>
                </div>
              </div>
              <button onClick={closeEditModal} className="text-gray-400 hover:text-gray-700 text-2xl font-bold transition">×</button>
            </div>

            {/* Edit Body */}
            <div className="overflow-y-auto flex-1 px-6 py-5">
              {/* Basic Fields */}
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div>
                  <label className={labelCls}>Farm / Company</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={editForm.farm}
                    onChange={e => setEditForm(f => f ? { ...f, farm: e.target.value } : f)}
                    placeholder="Farm name"
                  />
                </div>
                <div>
                  <label className={labelCls}>Party</label>
                  <select
                    className={inputCls}
                    value={editForm.party}
                    onChange={e => setEditForm(f => f ? { ...f, party: e.target.value } : f)}
                  >
                    <option value="">— Select Party —</option>
                    {parties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Date</label>
                  <input
                    type="date"
                    className={inputCls}
                    value={editForm.date}
                    onChange={e => setEditForm(f => f ? { ...f, date: e.target.value } : f)}
                  />
                </div>
              </div>

              {/* ── Purchase Rows ─────────────────────────────────────────── */}
              {editingInvoice.invoice_type?.toLowerCase() === "purchase" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[13px] font-bold text-yellow-800 uppercase tracking-wide">Purchase Items</div>
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-yellow-100 text-yellow-800 font-semibold text-xs border border-yellow-300 hover:bg-yellow-200 transition"
                      onClick={() => setEditForm(f => f ? { ...f, purchaseRows: [...f.purchaseRows, emptyPurchaseRow()] } : f)}
                    >+ Add Row</button>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-yellow-200">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="bg-yellow-50 text-yellow-900 font-semibold">
                          <th className="px-3 py-2 text-left">Stock Name</th>
                          <th className="px-3 py-2 text-center w-20">Units</th>
                          <th className="px-3 py-2 text-center w-28">Price/Unit (₹)</th>
                          <th className="px-3 py-2 text-center w-28">Indirect Charges</th>
                          <th className="px-3 py-2 text-center w-28">Total (₹)</th>
                          <th className="px-3 py-2 text-center w-24">Category</th>
                          <th className="px-3 py-2 text-center w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editForm.purchaseRows.map((row, i) => (
                          <tr key={i} className="border-t border-yellow-100 hover:bg-yellow-50/40">
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                className={inputCls}
                                value={row.stock_name}
                                placeholder="Stock name"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.purchaseRows];
                                  rows[i] = { ...rows[i], stock_name: e.target.value };
                                  return { ...f, purchaseRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-center"}
                                value={row.unit}
                                placeholder="0"
                                min={0}
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.purchaseRows];
                                  rows[i] = recalcPurchaseRow({ ...rows[i], unit: e.target.value });
                                  return { ...f, purchaseRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-right"}
                                value={row.perUnitCharge}
                                placeholder="0.00"
                                min={0}
                                step="0.01"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.purchaseRows];
                                  rows[i] = recalcPurchaseRow({ ...rows[i], perUnitCharge: e.target.value });
                                  return { ...f, purchaseRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-right"}
                                value={row.indirect_charges}
                                placeholder="0.00"
                                min={0}
                                step="0.01"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.purchaseRows];
                                  rows[i] = recalcPurchaseRow({ ...rows[i], indirect_charges: e.target.value });
                                  return { ...f, purchaseRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-right font-semibold bg-yellow-50"}
                                value={row.total_amount}
                                placeholder="0.00"
                                min={0}
                                step="0.01"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.purchaseRows];
                                  rows[i] = { ...rows[i], total_amount: e.target.value };
                                  return { ...f, purchaseRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                className={inputCls}
                                value={row.category}
                                placeholder="Category"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.purchaseRows];
                                  rows[i] = { ...rows[i], category: e.target.value };
                                  return { ...f, purchaseRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                className="text-red-400 hover:text-red-700 font-bold text-lg transition"
                                onClick={() => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = f.purchaseRows.filter((_, ri) => ri !== i);
                                  return { ...f, purchaseRows: rows.length ? rows : [emptyPurchaseRow()] };
                                })}
                              >×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-yellow-50 border-t-2 border-yellow-300 font-bold text-yellow-900">
                          <td className="px-3 py-2" colSpan={4}>Total</td>
                          <td className="px-3 py-2 text-right text-green-700 text-[14px]">
                            ₹{toCurrency(editForm.purchaseRows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0))}
                          </td>
                          <td colSpan={2} />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Sell Rows ─────────────────────────────────────────────── */}
              {editingInvoice.invoice_type?.toLowerCase() === "sell" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[13px] font-bold text-green-800 uppercase tracking-wide">Sell Items</div>
                    <button
                      type="button"
                      className="px-3 py-1 rounded bg-green-100 text-green-800 font-semibold text-xs border border-green-300 hover:bg-green-200 transition"
                      onClick={() => setEditForm(f => f ? { ...f, sellRows: [...f.sellRows, emptySellRow()] } : f)}
                    >+ Add Row</button>
                  </div>
                  <div className="overflow-x-auto rounded-xl border border-green-200">
                    <table className="w-full text-[12px]">
                      <thead>
                        <tr className="bg-green-50 text-green-900 font-semibold">
                          <th className="px-3 py-2 text-left">Stock Name</th>
                          <th className="px-3 py-2 text-center w-20">Units</th>
                          <th className="px-3 py-2 text-center w-28">Price/Unit (₹)</th>
                          <th className="px-3 py-2 text-center w-28">Indirect Charges</th>
                          <th className="px-3 py-2 text-center w-28">Total (₹)</th>
                          <th className="px-3 py-2 text-center w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {editForm.sellRows.map((row, i) => (
                          <tr key={i} className="border-t border-green-100 hover:bg-green-50/40">
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                className={inputCls}
                                value={row.stock_name}
                                placeholder="Stock name"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.sellRows];
                                  rows[i] = { ...rows[i], stock_name: e.target.value };
                                  return { ...f, sellRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-center"}
                                value={row.sell_units}
                                placeholder="0"
                                min={0}
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.sellRows];
                                  rows[i] = recalcSellRow({ ...rows[i], sell_units: e.target.value });
                                  return { ...f, sellRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-right"}
                                value={row.sell_amount}
                                placeholder="0.00"
                                min={0}
                                step="0.01"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.sellRows];
                                  rows[i] = recalcSellRow({ ...rows[i], sell_amount: e.target.value });
                                  return { ...f, sellRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-right"}
                                value={row.indirect_charges}
                                placeholder="0.00"
                                min={0}
                                step="0.01"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.sellRows];
                                  rows[i] = recalcSellRow({ ...rows[i], indirect_charges: e.target.value });
                                  return { ...f, sellRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                className={inputCls + " text-right font-semibold bg-green-50"}
                                value={row.total_amount}
                                placeholder="0.00"
                                min={0}
                                step="0.01"
                                onChange={e => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = [...f.sellRows];
                                  rows[i] = { ...rows[i], total_amount: e.target.value };
                                  return { ...f, sellRows: rows };
                                })}
                              />
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                type="button"
                                className="text-red-400 hover:text-red-700 font-bold text-lg transition"
                                onClick={() => setEditForm(f => {
                                  if (!f) return f;
                                  const rows = f.sellRows.filter((_, ri) => ri !== i);
                                  return { ...f, sellRows: rows.length ? rows : [emptySellRow()] };
                                })}
                              >×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-green-50 border-t-2 border-green-300 font-bold text-green-900">
                          <td className="px-3 py-2" colSpan={4}>Total</td>
                          <td className="px-3 py-2 text-right text-green-700 text-[14px]">
                            ₹{toCurrency(editForm.sellRows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0))}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {sellStocksMap[editingInvoice.id]?.length > 0 && (
                    <div className="mt-2 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded px-3 py-1.5">
                      ✓ This invoice has <b>{sellStocksMap[editingInvoice.id].length}</b> linked sell stock records — they will be updated automatically.
                    </div>
                  )}
                </div>
              )}

              {saveError && (
                <div className="mt-4 p-2 rounded bg-red-50 border border-red-200 text-red-700 text-[12.5px]">{saveError}</div>
              )}
            </div>

            {/* Edit Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-blue-100 bg-gray-50 flex-shrink-0">
              <button
                type="button"
                className="px-5 py-2 rounded-xl bg-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-300 transition"
                onClick={closeEditModal}
                disabled={saving}
              >Cancel</button>
              <button
                type="button"
                className="px-6 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold text-sm hover:from-amber-600 hover:to-amber-700 transition shadow disabled:opacity-60"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice View Modal ─────────────────────────────────────────────── */}
      {viewingInvoice && (
        <div className="fixed inset-0 z-50 flex flex-col h-screen w-screen">
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative flex flex-col flex-1 justify-center items-center h-full w-full py-7 px-2">
            <div className="bg-gradient-to-tr from-white to-blue-50 w-full max-w-[900px] h-full max-h-[97vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-blue-200">

              {/* Top bar */}
              <div className="flex justify-end gap-5 py-3 px-4 flex-shrink-0 bg-gradient-to-r from-blue-200/20 via-blue-100/5 to-white rounded-t-2xl border-b border-blue-100">
                <button onClick={handlePrint} className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-3 py-2 rounded-xl shadow hover:from-blue-400 hover:to-blue-200 transition text-[13px]">Print</button>
                <button
                  onClick={() => { setViewingInvoice(null); openEditModal(viewingInvoice); }}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-2 rounded-xl shadow hover:from-amber-600 hover:to-amber-700 transition text-[13px]"
                >Edit</button>
                <button onClick={() => openDeleteDialog(viewingInvoice)} className="bg-gradient-to-r from-red-500 to-red-700 text-white px-3 py-2 rounded-xl shadow hover:from-red-800 hover:to-red-900 transition text-[13px]">Delete</button>
                <button onClick={() => setViewingInvoice(null)} className="bg-gray-500 hover:bg-gray-800 text-white px-3 py-2 rounded-xl shadow transition text-[13px]">Close</button>
              </div>

              {/* Invoice Content */}
              <div id="invoice-print" className="p-2 text-[11.5px] text-gray-800 overflow-scroll">

                <div className="text-center py-4" style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "1px", color: "#1e3a8a", paddingTop: "24px", paddingBottom: "18px" }}>
                  INVOICE
                </div>

                <table className="w-full border border-blue-300 border-collapse rounded-2xl shadow mb-2">
                  <tbody>
                    <tr>
                      <td className="border p-2 w-1/2 align-top">
                        <div className="mb-1 text-xl text-blue-900 font-extrabold tracking-wide" style={{ fontSize: "14.5px", lineHeight: "1.2" }}>
                          {viewingInvoice.farm}
                        </div>
                      </td>
                      <td className="border p-2 align-top bg-gradient-to-t from-blue-50 to-blue-100">
                        <div className="grid grid-cols-2 gap-4 w-full">
                          <div className="mb-1 flex flex-col" style={{ fontSize: "12px" }}>
                            <b>Invoice No:</b>
                            <span className="text-blue-700">{viewingInvoice.id}</span>
                          </div>
                          <div className="mb-1 flex flex-col ml-8" style={{ fontSize: "12px" }}>
                            <b>Dated:</b>
                            <span className="text-blue-700">{formatDate(viewingInvoice.date)}</span>
                          </div>
                          <div className="mb-1 flex flex-col" style={{ fontSize: "12px" }}>
                            <b>Type:</b>
                            <span className={`inline-block px-2 py-0.5 rounded font-semibold border text-xs w-fit ${colorBadge(viewingInvoice.invoice_type)}`}>
                              {viewingInvoice.invoice_type}
                            </span>
                          </div>
                          <div className="mb-1 flex flex-col ml-8" style={{ fontSize: "12px" }}>
                            <b>Other Ref.:</b>
                            <span className="text-blue-700"></span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2 align-top bg-gradient-to-l from-pink-50 to-white/90" style={{ fontSize: "11.5px" }}>
                        <b className="text-blue-800">Consignee</b>
                        <div className="mb-1 mt-1"><b>{getPartyName(viewingInvoice.party)}</b></div>
                      </td>
                      <td className="border p-2 bg-white" />
                    </tr>
                  </tbody>
                </table>

                <table className="w-full border border-black border-collapse mt-[2px] mb-2 rounded-lg" style={{ fontSize: "11.5px", borderWidth: "1.2px" }}>
                  <thead>
                    <tr style={{ height: "22px", fontSize: "11.7px" }}>
                      <th className="border border-black" style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600 }}>Description of Goods</th>
                      <th className="border border-black" style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "80px" }}>Quantity</th>
                      <th className="border border-black" style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "110px" }}>Price / Unit (₹)</th>
                      <th className="border border-black" style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "70px" }}>Per</th>
                      <th className="border border-black" style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "75px" }}>Disc %</th>
                      <th className="border border-black" style={{ padding: "3px 6px", textAlign: "center", fontWeight: 600, width: "110px" }}>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rows = getInvoiceRows(viewingInvoice);
                      if (!rows.length) {
                        return (
                          <tr>
                            <td colSpan={6} className="py-4 text-center text-gray-400 italic font-semibold">No data rows in invoice</td>
                          </tr>
                        );
                      }
                      const totalUnits  = rows.reduce((s, r) => s + Number(r.unit || 0), 0);
                      const totalAmount = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
                      return (
                        <>
                          {rows.map((r, i) => (
                            <tr key={i}>
                              <td className="border border-black align-top" style={{ padding: "7px 10px 24px 7px", fontSize: "12.4px", minHeight: "90px" }}>
                                <div style={{ whiteSpace: "pre-line" }}>{r.stock_name}</div>
                              </td>
                              <td className="border border-black align-top text-center" style={{ padding: "7px 8px", fontSize: "12.6px" }}>
                                {r.unit !== undefined && r.unit !== null && r.unit !== "" ? `${r.unit} NOS` : "-"}
                              </td>
                              <td className="border border-black align-top text-right" style={{ padding: "7px 8px", fontSize: "12.6px" }}>
                                {r.perUnitCharge ? toCurrency(r.perUnitCharge) : "-"}
                              </td>
                              <td className="border border-black align-top text-center" style={{ padding: "7px 8px", fontSize: "12.6px" }}>NOS</td>
                              <td className="border border-black align-top text-right" style={{ padding: "7px 8px", fontSize: "12.6px" }}>—</td>
                              <td className="border border-black align-top text-right" style={{ padding: "7px 8px", fontSize: "12.6px" }}>
                                {toCurrency(r.total_amount)}
                              </td>
                            </tr>
                          ))}
                          <tr className="font-bold bg-green-50 invoice-total">
                            <td className="border p-2 text-right" colSpan={1}>Total</td>
                            <td className="border p-2 text-center text-green-700 text-[15px] font-bold">{totalUnits}</td>
                            <td className="border p-2 text-center" colSpan={3} />
                            <td className="border p-2 text-right text-green-700 text-[16px] font-bold">₹{toCurrency(totalAmount)}</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>

                <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 mb-2 flex flex-col">
                  <span className="text-lg font-bold text-blue-900 mb-1" style={{ fontSize: "12.5px" }}>Amount Chargeable (in words)</span>
                  <span className="text-[11.5px] text-gray-700 font-mono">
                    {(() => {
                      const rows  = getInvoiceRows(viewingInvoice);
                      const total = rows.reduce((s, r) => s + Number(r.total_amount || 0), 0);
                      return numberToWords(total) + " Rupees only.";
                    })()}
                  </span>
                </div>

                <div className="w-full border border-blue-200 border-collapse mt-[4px] rounded-lg overflow-hidden flex flex-row items-stretch bg-white">
                  <div className="w-1/2 border-r border-blue-200 p-2 flex flex-col justify-between bg-gray-50" style={{ fontSize: "11px" }}>
                    <div>
                      <b className="text-blue-900 mb-1 block">Declaration</b>
                      <div className="text-gray-800 mt-1">
                        We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
                      </div>
                    </div>
                  </div>
                  <div className="w-1/2 p-2 flex flex-row justify-between text-left bg-white">
                    <div className="flex flex-col w-full border border-blue-200 border-collapse mt-1 rounded-lg overflow-hidden">
                      <div className="border p-2 w-full align-top bg-gray-50 rounded-bl-lg flex flex-col" style={{ fontSize: "11px" }}>
                        <div className="mt-1 mb-1">Company Sales Tax No.</div>
                        <div>Buyer's Sales Tax No.</div>
                        <div>Buyer's Inter State Sales Tax No.</div>
                      </div>
                      <div className="border border-l-0 p-2 w-full text-right align-top bg-white rounded-br-lg flex flex-col justify-between">
                        <div>
                          <div className="mb-2">For <b>{viewingInvoice.farm}</b></div>
                          <div className="text-gray-400 italic text-sm text-center" style={{ fontSize: "10px" }}>No signature on file</div>
                          <div className="pt-1 font-semibold" style={{ fontSize: "10.5px" }}>Authorised Signatory</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InvoicePage;